import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UploadInstanceResponse {
  facilityId: string;
  instanceId: string;
  tags?: Record<string, string>;
  fileSize?: number;
}

export interface StudyItem {
  studyInstanceUid: string;
  patientName?: string;
  patientId?: string;
  accessionNumber?: string;
  studyDate?: string; // YYYY-MM-DD or YYYYMMDD
  studyTime?: string; // HHMMSS or HH:MM:SS
  studyDescription?: string;
  studyId?: string;
  modalitiesInStudy?: string[];
  seriesCount?: number;
  instanceCount?: number;
  // Additional DICOM fields
  referringPhysicianName?: string;
  requestingPhysicianName?: string;
  institutionName?: string;
  patientBirthDate?: string; // YYYYMMDD or YYYY-MM-DD
  patientSex?: string;
  patientOtherIds?: string;
}

export interface StudiesResponse {
  pageSize: number;
  nextPageToken?: string | null;
  items: StudyItem[];
}

export interface StudySearchFilters {
  q?: string;
  patientName?: string;
  patientId?: string;
  accessionNumber?: string;
  institutionName?: string;
  studyDateFrom?: string; // YYYY-MM-DD
  studyDateTo?: string; // YYYY-MM-DD
  modality?: string;
  pageSize?: number;
  pageToken?: string;
}

export interface StudySeries {
  seriesInstanceUid?: string;
  seriesNumber?: string;
  modality?: string;
  seriesDescription?: string;
  seriesDate?: string;
  seriesTime?: string;
  instanceCount?: number;
  instances?: Array<{ instanceId: string }>;
}

export interface StudyDetails extends StudyItem {
  orthancId: string;
  series: StudySeries[];
  fullMetadata?: {
    study: any;
    seriesCount: number;
    totalInstances: number;
  };
}

@Injectable({ providedIn: 'root' })
export class StudiesApiService {
  private readonly baseUrl = environment.bffBaseUrl;

  constructor(private http: HttpClient) {}

  searchStudies(filters: StudySearchFilters): Observable<StudiesResponse> {
    let params = new HttpParams();

    // BFF requires at least one of: patientName, patientId, or accessionNumber
    // Map the filters to match BFF expectations
    if (filters.patientName) {
      params = params.set('patientName', String(filters.patientName));
    }
    if (filters.patientId) {
      params = params.set('patientId', String(filters.patientId));
    }
    if (filters.accessionNumber) {
      params = params.set('accessionNumber', String(filters.accessionNumber));
    }
    if (filters.institutionName) {
      params = params.set('institutionName', String(filters.institutionName));
    }
    if (filters.modality) {
      params = params.set('modality', String(filters.modality));
    }
    if (filters.studyDateFrom) {
      params = params.set('studyDateFrom', String(filters.studyDateFrom));
    }
    if (filters.studyDateTo) {
      params = params.set('studyDateTo', String(filters.studyDateTo));
    }
    if (filters.pageSize) {
      params = params.set('pageSize', String(filters.pageSize));
    }
    if (filters.pageToken) {
      params = params.set('pageToken', String(filters.pageToken));
    }

    return this.http.get<StudiesResponse>(`${this.baseUrl}/studies`, { params });
  }

  /**
   * Get detailed information about a specific study
   * @param studyInstanceUid - The StudyInstanceUID of the study
   * @returns Observable with full study details including series and instances
   */
  getStudy(studyInstanceUid: string): Observable<StudyDetails> {
    return this.http.get<StudyDetails>(`${this.baseUrl}/studies/${studyInstanceUid}`);
  }

  /**
   * Download one or more studies as ZIP archive(s)
   * @param studyInstanceUids - Array of StudyInstanceUIDs to download
   * @returns Observable with Blob containing the ZIP file(s)
   */
  downloadStudy(studyInstanceUids: string[]): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}/exports/studies`,
      { studyInstanceUids },
      { responseType: 'blob' }
    );
  }

  /**
   * Upload a DICOM instance file to the current user's facility Orthanc via the BFF.
   * Only owners are allowed to upload (enforced by BFF).
   */
  uploadDicomInstance(file: Blob): Observable<UploadInstanceResponse> {
    const url = this.baseUrl.replace(/\/+$/, '') + '/instances';
    return this.http.post<UploadInstanceResponse>(url, file, {
      headers: {
        'Content-Type': 'application/dicom',
      },
    });
  }
}
